import ApiController from '@/services/ApiController'
import { useCustomerStore } from '@/stores/customer'
import { defineStore, storeToRefs } from 'pinia'

interface AddressesStoreState {
  addresses: any[] | null
}

export const useAddressesStore = defineStore('addresses', {
  state: (): AddressesStoreState => ({
    addresses: null
  }),
  getters: {
    hasAddresses: ({ addresses }): boolean =>
      !!addresses && addresses.length > 0,
    getAddressById:
      ({ addresses }) =>
      (id: number): object | null => {
        return addresses
          ? (addresses.find(
              (address) => +address.customer_address_id === +id
            ) ?? null)
          : null
      },
    getDefaultShippingAddress:
      ({ addresses }) =>
      (fallback: boolean = false): object | null => {
        const defaultAddress = addresses?.find(
          (a) => a.is_default_shipping_address
        )
        if (defaultAddress) return defaultAddress
        if (fallback && addresses && addresses.length > 0) return addresses[0]
        return null
      },
    getDefaultBillingAddress:
      ({ addresses }) =>
      (fallback: boolean = false): object | null => {
        const defaultAddress = addresses?.find(
          (a) => a.is_default_billing_address
        )
        if (defaultAddress) return defaultAddress
        if (fallback && addresses && addresses.length > 0) return addresses[0]
        return null
      }
  },
  actions: {
    async fetch() {
      const customerStore = useCustomerStore()
      const { customerActive } = storeToRefs(customerStore)
      await customerStore.waitUntilPending()
      if (!customerActive.value) {
        this.addresses = null
        return
      }
      try {
        this.addresses = await ApiController.fetchCustomerAddresses()
      } catch (e) {
        console.error(e)
      }
    },
    async saveAddress(data: object): Promise<number | null> {
      const addressData = _getAddressData(data)
      const response = await ApiController.saveCustomerAddress(addressData)
      void this.fetch()
      return response?.customer_address_id
    },
    async updateAddress(data: object): Promise<void> {
      await ApiController.updateCustomerAddress(data)
      void this.fetch()
    },
    async deleteAddress(id: number): Promise<void> {
      await ApiController.deleteCustomerAddress(id)
      void this.fetch()
    },
    getEmptyAddress(): object {
      return _getAddressData({})
    }
  }
})

const _getAddressData = (data: any) => {
  return {
    title: data.title ?? null,
    first_name: data.first_name,
    last_name: data.last_name,
    gender: data.gender,
    company: data.company ?? null,
    street_name: data.street_name,
    street_number: data.street_number,
    additional: data.additional ?? null,
    zip: data.zip,
    city: data.city,
    country: data.country,
    is_default_billing_address: data.is_default_billing_address ?? false,
    is_default_shipping_address: data.is_default_shipping_address ?? false
  }
}
